import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import api from "@/lib/api";
import { FileText, FileCheck2, Droplets, BellRing, Activity, Plus, AlertTriangle } from "lucide-react";

export default function InstallerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const cards = [
    { label: "Available Credits", value: stats?.available_credits ?? 0, icon: FileText, accent: true, sub: "Certificate credits" },
    { label: "Certificates Used", value: stats?.certificates_used ?? 0, icon: FileCheck2, sub: "Issued to date" },
    { label: "Bottle Activations", value: stats?.bottle_activations ?? 0, icon: Droplets, sub: "Bottles activated" },
    { label: "Follow-Ups Due", value: stats?.follow_ups_due ?? 0, icon: BellRing, sub: "30-day reminders", onClick: () => navigate("/follow-ups") },
  ];

  const credits = stats?.available_credits ?? null;
  const overdue = stats?.follow_ups_overdue ?? 0;
  const recentCompleted = stats?.recent_completed_follow_ups ?? [];
  const recent = stats?.recent_activity ?? [];

  return (
    <DashboardLayout title="Dashboard">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Overview</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">
            Welcome back, {user?.contact_name?.split(" ")[0] || "Installer"}
          </h2>
          <p className="mt-1 text-sm text-neutral-500">Here's a snapshot of your SGBINOXE activity.</p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <button
            onClick={() => navigate("/bottle-activations")}
            className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 transition-colors hover:bg-white/[0.05]"
            data-testid="dashboard-activate-bottle-btn"
          >
            <Droplets className="h-4 w-4" /> Activate Bottle
          </button>
          <button
            onClick={() => navigate("/certificates/new")}
            className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-[#FF1C2A]"
            data-testid="dashboard-new-certificate-btn"
          >
            <Plus className="h-4 w-4" />
            New Installation Certificate
          </button>
        </div>
      </div>

      {stats?.grace?.active && (
        <div className="mb-6 overflow-hidden rounded-2xl border border-[#E30613]/40 bg-gradient-to-br from-[#E30613]/[0.12] to-[#141414] p-6" data-testid="grace-period-card">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E30613]/20"><AlertTriangle className="h-5 w-5 text-[#FF6069]" /></div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF6069]">Account Status</p>
                <h3 className="mt-1 font-display text-xl font-semibold text-white">Grace Period Active</h3>
                <p className="mt-1 max-w-md text-sm text-neutral-400" data-testid="grace-notification">
                  You have <span className="font-semibold text-white">0 available credits</span>. Your account will become inactive in{" "}
                  <span className="font-semibold text-[#FF6069]">{stats.grace.days_remaining} day{stats.grace.days_remaining !== 1 ? "s" : ""}</span> unless you activate another SGBINOXE bottle.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-5 self-start sm:self-center">
              <div className="text-right">
                <p className="font-display text-3xl font-bold text-white" data-testid="grace-days-remaining">{stats.grace.days_remaining}</p>
                <p className="text-xs text-neutral-500">Days Remaining</p>
              </div>
              <button onClick={() => navigate("/bottle-activations")} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#FF1C2A]" data-testid="grace-redeem-btn">
                <Droplets className="h-4 w-4" /> Redeem Bottle
              </button>
            </div>
          </div>
        </div>
      )}
      {credits !== null && credits === 0 && !stats?.grace?.active && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-[#E30613]/30 bg-[#E30613]/10 px-5 py-4 text-sm text-[#FF6069]" data-testid="zero-credits-warning">
          <BellRing className="h-4 w-4" />
          You have no certificate credits remaining. Activate another SGBINOXE bottle to generate a new certificate.
        </div>
      )}
      {credits !== null && credits > 0 && credits <= 3 && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-sm text-amber-400" data-testid="low-credits-warning">
          <BellRing className="h-4 w-4" />
          You are running low on certificate credits. Activate another SGBINOXE bottle to continue generating certificates.
        </div>
      )}
      {overdue > 0 && (
        <button
          onClick={() => navigate("/follow-ups")}
          className="mb-6 flex w-full items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-left text-sm text-amber-400 transition-colors hover:bg-amber-500/[0.16]"
          data-testid="overdue-followups-banner"
        >
          <BellRing className="h-4 w-4 shrink-0" />
          You have {overdue} customer follow-up{overdue > 1 ? "s" : ""} waiting. Reach out to keep your customers delighted.
        </button>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c, i) => (
          <StatCard key={c.label} {...c} delay={i * 70} />
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-white/[0.06] bg-[#141414] p-6">
        <div className="mb-5 flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#FF1C2A]" />
          <h3 className="font-display text-base font-medium text-white">Recent Credit Activity</h3>
        </div>
        {recent.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/[0.08] py-16 text-center">
            <p className="text-sm text-neutral-500">No activity yet</p>
            <p className="mt-1 text-xs text-neutral-600">Bottle activations and certificate credits will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {recent.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-3" data-testid="recent-activity-row">
                <div>
                  <p className="text-sm text-white">{t.transaction_type}</p>
                  <p className="text-xs text-neutral-500">{new Date(t.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${t.credit_amount >= 0 ? "text-emerald-400" : "text-[#FF6069]"}`}>{t.credit_amount >= 0 ? "+" : ""}{t.credit_amount}</p>
                  <p className="text-xs text-neutral-500">Balance {t.balance_after}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {recentCompleted.length > 0 && (
        <div className="mt-6 rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="recent-completed-followups">
          <div className="mb-5 flex items-center gap-2">
            <FileCheck2 className="h-4 w-4 text-emerald-400" />
            <h3 className="font-display text-base font-medium text-white">Recently Completed Follow-Ups</h3>
          </div>
          <div className="divide-y divide-white/[0.05]">
            {recentCompleted.map((f) => (
              <button key={f.id} onClick={() => navigate(`/follow-ups/${f.id}`)} className="flex w-full items-center justify-between py-3 text-left hover:opacity-80" data-testid="completed-followup-row">
                <div>
                  <p className="text-sm text-white">{f.customer_name}</p>
                  <p className="text-xs text-neutral-500">{f.vehicle_year} {f.vehicle_make} {f.vehicle_model} · {f.certificate_number}</p>
                </div>
                <span className="text-xs text-emerald-400">Completed</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
